// Department slip endpoints (OPD) — /app/api/department_slip/*
//
//   GET  departments/                              medical + standalone physical departments
//   GET  service_department/?medical_service_id=   a service's current physical department
//   POST service_department/                       tag a service (exactly one of
//                                                  medical_department_id / physical_department_id /
//                                                  new_physical_department_name)
//   GET  pdf/?invoice_id=[&slip_id=]               PDF, one page per department slip
//
// APP-MODE / ORG-CODE / USER-ACCESS-KEY / USERNAME come from the global axios
// interceptor; only the bearer token is attached here.

import axios from "axios";

const SLIP_ROOT = `${process.env.REACT_APP_BACKEND_ROUTE_URL}department_slip/`;

const authHeaders = () => ({
  Authorization: `Bearer ${sessionStorage.getItem("accessToken") || ""}`,
  "Content-Type": "application/json",
});

const unwrap = (body) =>
  body && body.data && typeof body.data === "object" && !Array.isArray(body.data) ? body.data : body;

const assertSuccess = (body, fallback) => {
  if (body?.status && String(body.status).toLowerCase() !== "success") {
    throw new Error(body.message || fallback);
  }
  return body;
};

const errorMessage = (err, fallback) => err?.response?.data?.message || err?.message || fallback;

/**
 * { medical: [{ id, name, physicalDepartmentId }], physical: [{ id, name }] }
 * `medical[].id` is MedicalDepartment.department_id (the string key), which is
 * what the backend expects as `medical_department_id`.
 */
export async function getSlipDepartments() {
  try {
    const res = await axios.get(`${SLIP_ROOT}departments/`, { headers: authHeaders() });
    const body = unwrap(assertSuccess(res.data, "Could not load departments"));
    return {
      medical: (body?.medical_departments || []).map((d) => ({
        id: String(d.department_id ?? d.medical_department_id ?? d.id),
        name: d.department_name ?? d.department ?? d.name ?? "—",
        physicalDepartmentId: d.physical_department_id ?? null,
      })),
      physical: (body?.physical_departments || []).map((d) => ({
        id: d.id ?? d.physical_department_id,
        name: d.name ?? d.department_name ?? "—",
      })),
    };
  } catch (err) {
    throw new Error(errorMessage(err, "Could not load departments"));
  }
}

/** Current physical department id of a service, or null when untagged. */
export async function getServiceDepartment(medicalServiceId) {
  try {
    const res = await axios.get(`${SLIP_ROOT}service_department/`, {
      headers: authHeaders(),
      params: { medical_service_id: medicalServiceId },
    });
    const body = unwrap(assertSuccess(res.data, "Could not load service department"));
    return body?.physical_department_id ?? body?.physical_department?.id ?? null;
  } catch (err) {
    throw new Error(errorMessage(err, "Could not load service department"));
  }
}

/**
 * `choice` carries exactly one of medical_department_id, physical_department_id
 * (null removes the tag) or new_physical_department_name.
 */
export async function setServiceDepartment(medicalServiceId, choice) {
  try {
    const res = await axios.post(
      `${SLIP_ROOT}service_department/`,
      { medical_service_id: medicalServiceId, ...choice },
      { headers: authHeaders() }
    );
    return assertSuccess(res.data, "Could not set service department");
  } catch (err) {
    throw new Error(errorMessage(err, "Could not set service department"));
  }
}

/**
 * Fetch the slip PDF for an invoice. Resolves to a PDF Blob, or null when the
 * invoice has no active slips (backend answers with JSON instead of a PDF).
 */
export async function fetchDepartmentSlipPdf(invoiceId, slipId) {
  let res;
  try {
    res = await axios.get(`${SLIP_ROOT}pdf/`, {
      headers: authHeaders(),
      params: { invoice_id: invoiceId, ...(slipId != null && { slip_id: slipId }) },
      responseType: "blob",
    });
  } catch (err) {
    // A 4xx carries a JSON body inside the blob.
    const blob = err?.response?.data;
    if (blob instanceof Blob) {
      let message = "";
      try {
        message = JSON.parse(await blob.text())?.message || "";
      } catch (e) {
        // not JSON
      }
      if (err.response.status === 404) return null;
      throw new Error(message || "Could not load department slips");
    }
    throw new Error(errorMessage(err, "Could not load department slips"));
  }
  const type = res.headers?.["content-type"] || "";
  if (!type.includes("pdf")) return null;
  return new Blob([res.data], { type: "application/pdf" });
}
